import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, Fragment as _Fragment, createElementBlock as _createElementBlock, createSlots as _createSlots } from "vue"

const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", null, "Hieronder vind u een overzicht van alle opleverpunten en servicemeldingen van al uw bouwnummers.", -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("p", { class: "no-mb" }, [
  /*#__PURE__*/_createTextVNode(" Wilt u een servicemelding doen of een reparatieverzoek indienen? Heeft u de handleiding(en) bij de documenten al bekeken? Wellicht vindt u daar eenvoudig en snel een antwoord op uw vraag. "),
  /*#__PURE__*/_createElementVNode("br"),
  /*#__PURE__*/_createTextVNode(" Klik anders rechtsboven op \"Nieuwe servicemelding\" om een nieuwe servicemelding aan te maken. ")
], -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("label", null, "Zoeken", -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("br", null, null, -1)

import { useRouter } from 'vue-router'
import { issueStore } from '@/stores/issue'
import { objectionStore } from '@/stores/objection'
import { computed, onMounted, ref } from 'vue'
import { IssueStatusColors } from '@/globalVariables'
import { generalStore } from '@/stores/general'
import axios from 'axios'
import { ls } from '@/ls'
import { authStore } from '@/stores/auth'

interface Project {
  id:number
  name:string
}

interface Building {
  id:number
  number:number
  address:number
}

interface Status {
  id:number
  name:string
}

interface Issue {
  id:number
  reference:string
  title:string
  type:number
  status:Status
  created_at:string
  updated_at:string
  project:Project
  building:Building
}

interface Filter {
  text:string,
  value:number
}

interface Column {
  title:string,
  key:string
  filters?:Filter[]
  onFilter?: (value: number, record: Issue) => boolean
  filteredValue?:number[]|string[]|null
  defaultSortOrder?:string|null
  sorter?: (a: Issue, b: Issue) => number
}

interface TablePagination {
  project: number[]|null
  status: number[]|null
}

interface TableFilters {
  project: number[]|null
  type: number[]|null
  status: number[]|null
}

interface TableSorter {
  columnKey: string|undefined
  order: string|undefined
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Issues',
  setup(__props) {

const router = useRouter()

const filteredColumns = computed(() => {
  return columns.value
})
const columns = ref<Column[]>([
  {
    title: 'Ref. nr.',
    key: 'reference',
    defaultSortOrder: ls.get('globalIssuesSort').columnKey === 'reference' ? ls.get('globalIssuesSort').order : null,
    sorter: (a: Issue, b: Issue) => a.id - b.id
  },
  {
    title: 'Type',
    key: 'type',
    filters: [],
    onFilter: (value:number, record:Issue) => record.type === value,
    filteredValue: ls.get('globalIssuesFilters').type || null
  },
  {
    title: 'Project',
    key: 'project',
    filters: [],
    onFilter: (value:number, record:Issue) => record.project.id === value,
    filteredValue: ls.get('globalIssuesFilters').project || null
  },
  {
    title: 'Bouwnummer',
    key: 'building.number',
    defaultSortOrder: ls.get('globalIssuesSort').columnKey === 'building.number' ? ls.get('globalIssuesSort').order : null,
    sorter: (a: Issue, b: Issue) => a.building.number - b.building.number
  },
  {
    title: 'Adres',
    key: 'building.address'
  },
  {
    title: 'Onderwerp',
    key: 'subject'
  },
  {
    title: 'Gemeld op',
    key: 'created_at',
    defaultSortOrder: ls.get('globalIssuesSort').columnKey === 'created_at' ? ls.get('globalIssuesSort').order : null,
    sorter: (a: Issue, b: Issue) => {
      const dateA = new Date(a.created_at)
      const dateB = new Date(b.created_at)

      return dateA.getTime() - dateB.getTime()
    }
  },
  {
    title: 'Bijgewerkt op',
    key: 'updated_at',
    defaultSortOrder: ls.get('globalIssuesSort').columnKey === 'updated_at' ? ls.get('globalIssuesSort').order : null,
    sorter: (a: Issue, b: Issue) => {
      const dateA = new Date(a.updated_at)
      const dateB = new Date(b.updated_at)

      return dateA.getTime() - dateB.getTime()
    }
  },
  {
    title: 'Status',
    key: 'status',
    filters: [],
    onFilter: (value:number, record:Issue) => record.status.id === value,
    filteredValue: ls.get('globalIssuesFilters').status || null
  }
])

const loading = ref(false)
const searchTerm = ref<string|null>(null)
const issues = ref([])

onMounted(() => {
  getIssueOverview()
})

function getIssueOverview () : void {
  loading.value = true
  const searchQuery = searchTerm.value ? '?search=' + searchTerm.value : ''
  axios.get(process.env.VUE_APP_API_URL + '/issues/all' + searchQuery).then((r) => {
    issues.value = r.data
    setProjectFilter()
    setTypeFilter()
    setStatusFilter()
  }).catch(() => {
    console.log('Error while loading issues!')
  }).finally(() => {
    loading.value = false
  })
}

function setProjectFilter (): void {
  const projectFilters: Filter[] = []
  const addedProjectIds = new Set<number>()

  issues.value.forEach((i: Issue) => {
    // Check if project id has already been added
    if (!addedProjectIds.has(i.project.id)) {
      // If not added, push to projectFilters and mark id as added
      projectFilters.push({ text: i.project.name, value: i.project.id })
      addedProjectIds.add(i.project.id)
    }
  })

  columns.value[2].filters = projectFilters
}

function setTypeFilter (): void {
  const typeFilters: Filter[] = []
  const addedTypeIds = new Set<number>()

  issues.value.forEach((i: Issue) => {
    // Check if status id has already been added
    if (!addedTypeIds.has(i.type)) {
      // If not added, push to statusFilters and mark id as added
      typeFilters.push({ text: i.type === 4 ? 'Opleverpunt' : 'Servicemelding', value: i.type })
      addedTypeIds.add(i.type)
    }
  })

  columns.value[1].filters = typeFilters
}

function setStatusFilter (): void {
  const statusFilters: Filter[] = []
  const addedStatusIds = new Set<number>()

  issues.value.forEach((i: Issue) => {
    // Check if status id has already been added
    if (!addedStatusIds.has(i.status.id)) {
      // If not added, push to statusFilters and mark id as added
      statusFilters.push({ text: i.status.name, value: i.status.id })
      addedStatusIds.add(i.status.id)
    }
  })

  columns.value[8].filters = statusFilters
}

function onRowClick (record:{ id:number }) {
  return {
    onClick: () => {
      router.push({ name: 'Global/IssueShow', params: { issueId: record.id.toString() } })
    }
  }
}

function tableChanged (pagination:TablePagination, filters:TableFilters, sorter:TableSorter) {
  ls.set('globalIssuesFilters', filters)
  ls.set('globalIssuesSort', { columnKey: sorter.columnKey, order: sorter.order })

  storeUserSetting('globalIssuesFilters', filters)
  storeUserSetting('globalIssuesSort', { columnKey: sorter.columnKey, order: sorter.order })

  columns.value[1].filteredValue = filters.type
  columns.value[2].filteredValue = filters.project
  columns.value[8].filteredValue = filters.status
}

function storeUserSetting (key:string, value:TableFilters|TableSorter) {
  axios.post(process.env.VUE_APP_API_URL + '/users/buyersPortal/settings', {
    settingName: key,
    settingValue: value
  })
}

return (_ctx: any,_cache: any) => {
  const _component_a_alert = _resolveComponent("a-alert")!
  const _component_a_col = _resolveComponent("a-col")!
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_input_search = _resolveComponent("a-input-search")!
  const _component_a_tag = _resolveComponent("a-tag")!
  const _component_a_table = _resolveComponent("a-table")!
  const _component_a_row = _resolveComponent("a-row")!
  const _component_a_card = _resolveComponent("a-card")!

  return (_openBlock(), _createBlock(_component_a_row, { gutter: [16,16] }, {
    default: _withCtx(() => [
      (_unref(generalStore)().appSettings?.notification?.pages.includes('Service'))
        ? (_openBlock(), _createBlock(_component_a_col, {
            key: 0,
            xs: {span: 24},
            sm: {span: 24},
            md: {span: 24},
            lg: {span: 18, offset: 3}
          }, {
            default: _withCtx(() => [
              _createVNode(_component_a_alert, {
                type: "info",
                class: "global-notification mb-16",
                message: _unref(generalStore)().appSettings.notification.title,
                "show-icon": ""
              }, {
                description: _withCtx(() => [
                  _createElementVNode("div", {
                    innerHTML: _unref(generalStore)().appSettings.notification.description
                  }, null, 8, _hoisted_1)
                ]),
                _: 1
              }, 8, ["message"])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(_component_a_col, {
        xs: {span: 24},
        sm: {span: 24},
        md: {span: 24},
        lg: {span: 24}
      }, {
        default: _withCtx(() => [
          _createVNode(_component_a_card, {
            title: "Servicemeldingen",
            loading: _unref(issueStore)().loading || _unref(objectionStore)().loading
          }, _createSlots({
            default: _withCtx(() => [
              _createVNode(_component_a_row, { gutter: [16,16] }, {
                default: _withCtx(() => [
                  _createVNode(_component_a_col, { span: 24 }, {
                    default: _withCtx(() => [
                      _hoisted_2,
                      _hoisted_3
                    ]),
                    _: 1
                  }),
                  (issues.value.length)
                    ? (_openBlock(), _createBlock(_component_a_col, {
                        key: 0,
                        span: 24
                      }, {
                        default: _withCtx(() => [
                          _hoisted_4,
                          _hoisted_5,
                          _createVNode(_component_a_input_search, {
                            placeholder: "Zoek op: Referentie, Adres of Onderwerp..",
                            "enter-button": "",
                            "allow-clear": "",
                            value: searchTerm.value,
                            "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((searchTerm).value = $event)),
                            onSearch: getIssueOverview
                          }, null, 8, ["value"])
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _createVNode(_component_a_col, { span: 24 }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_table, {
                        columns: columns.value,
                        "data-source": issues.value,
                        loading: loading.value,
                        pagination: false,
                        "custom-row": onRowClick,
                        "row-key": "id",
                        "row-class-name": "clickable-row",
                        onChange: tableChanged
                      }, {
                        bodyCell: _withCtx(({ column, record }) => [
                          (column.key === 'project')
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _createTextVNode(_toDisplayString(record.project.name), 1)
                              ], 64))
                            : _createCommentVNode("", true),
                          (column.key === 'building.number')
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createTextVNode("Bouwnummer " + _toDisplayString(record.building.number), 1)
                              ], 64))
                            : _createCommentVNode("", true),
                          (column.key === 'building.address')
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                _createTextVNode(_toDisplayString(record.building.address), 1)
                              ], 64))
                            : _createCommentVNode("", true),
                          (column.key === 'type')
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                                (record.type === 4)
                                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                      _createTextVNode("Opleverpunt")
                                    ], 64))
                                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                      _createTextVNode("Servicemelding")
                                    ], 64))
                              ], 64))
                            : _createCommentVNode("", true),
                          (column.key === 'reference')
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                                _createTextVNode(_toDisplayString(record.id), 1)
                              ], 64))
                            : _createCommentVNode("", true),
                          (column.key === 'subject')
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                                _createTextVNode(_toDisplayString(record.title), 1)
                              ], 64))
                            : _createCommentVNode("", true),
                          (column.key === 'created_at')
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 6 }, [
                                _createTextVNode(_toDisplayString(new Date(record.created_at).toLocaleDateString('nl-NL')), 1)
                              ], 64))
                            : _createCommentVNode("", true),
                          (column.key === 'updated_at')
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 7 }, [
                                _createTextVNode(_toDisplayString(new Date(record.updated_at).toLocaleDateString('nl-NL')), 1)
                              ], 64))
                            : _createCommentVNode("", true),
                          (column.key === 'status')
                            ? (_openBlock(), _createBlock(_component_a_tag, {
                                key: 8,
                                color: _unref(IssueStatusColors)[record.status.id]
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(record.status.name), 1)
                                ]),
                                _: 2
                              }, 1032, ["color"]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      }, 8, ["columns", "data-source", "loading"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 2
          }, [
            (_unref(generalStore)().desktop)
              ? {
                  name: "extra",
                  fn: _withCtx(() => [
                    _createVNode(_component_a_button, {
                      disabled: _unref(authStore)().restricted(),
                      type: "primary",
                      onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).push({ name: 'Global/IssueCreate' })))
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode("Nieuwe servicemelding")
                      ]),
                      _: 1
                    }, 8, ["disabled"])
                  ]),
                  key: "0"
                }
              : undefined
          ]), 1032, ["loading"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})